import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
  Chip
} from '@material-ui/core';
import {
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Queue as QueueIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Assessment as AssessmentIcon,
  HourglassEmpty as HourglassEmptyIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

import toastUtils from '../../utils/toastUtils';
import { useTimeMonitoring } from '../../hooks/useTimeMonitoring';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  cardSection: {
    marginBottom: '16px'
  },
  statusChip: {
    marginLeft: '8px'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    flexDirection: 'column'
  },
  statsCard: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    marginTop: '16px'
  }
}));

// Componentes customizados seguindo padrão IA.js
const SettingSwitch = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  disabled = false,
  icon,
  loading = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.checked);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip
          icon={value ? <CheckCircleIcon /> : <BlockIcon />}
          label={value ? i18n.t("timeMonitoring.enabled") : i18n.t("timeMonitoring.disabled")}
          color={value ? "primary" : "default"}
          size="small"
          className={classes.statusChip}
        />
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={value}
          onChange={handleChange}
          disabled={disabled || loading}
          style={{ marginRight: '8px' }}
        />
        <Typography variant="body2">
          {loading ? i18n.t("timeMonitoring.updating") : (value ? i18n.t("timeMonitoring.enabled") : i18n.t("timeMonitoring.disabled"))}
        </Typography>
      </label>
    </Box>
  );
});

const SettingTextField = React.memo(({
  id,
  title,
  description,
  value,
  onChange,
  icon,
  type = "text",
  min,
  max,
  helperText,
  disabled = false,
  loading = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    const newValue = type === "number" ? parseInt(event.target.value) : event.target.value;
    onChange(id, newValue);
  }, [id, onChange, type]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <input
        type={type}
        value={value || ""}
        onChange={handleChange}
        disabled={disabled || loading}
        min={min}
        max={max}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}
      />
      {helperText && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
          {helperText}
        </Typography>
      )}
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
          {i18n.t("timeMonitoring.updating")}
        </Typography>
      )}
    </Box>
  );
});

const SettingSelect = React.memo(({
  id,
  title,
  description,
  value,
  options,
  onChange,
  icon,
  disabled = false,
  loading = false
}) => {
  const classes = useStyles();
  const handleChange = useCallback((event) => {
    onChange(id, event.target.value);
  }, [id, onChange]);

  return (
    <Box className={classes.settingContainer}>
      <Box className={classes.sectionHeader}>
        {icon}
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {description}
        </Typography>
      )}
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || loading}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          fontSize: '14px',
          fontFamily: 'inherit'
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {loading && (
        <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px' }}>
          {i18n.t("timeMonitoring.updating")}
        </Typography>
      )}
    </Box>
  );
});

const TimeMonitoringSettings = () => {
  const classes = useStyles();
  const {
    settings,
    loading,
    error,
    saveSettings,
    getUserTicketStats
  } = useTimeMonitoring();

  const [formData, setFormData] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    warning: false,
    actions: false,
    inactivity: true,
    stats: false
  });

  // Carregar dados auxiliares (usuários e filas)
  useEffect(() => {
    const loadAuxiliaryData = async () => {
      try {
        const [usersResponse, queuesResponse] = await Promise.all([
          api.get('/time-monitoring/available-users'),
          api.get('/time-monitoring/available-queues')
        ]);

        setUsers(usersResponse.data || []);
        setQueues(queuesResponse.data || []);
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares:', error);
        toastUtils.error(i18n.t("timeMonitoring.errors.title"), i18n.t("timeMonitoring.errors.loadData"));
      }
    };

    loadAuxiliaryData();
  }, []);

  // Sincronizar formData com settings
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  // Carregar estatísticas do usuário
  useEffect(() => {
    if (settings.limitActiveTicketsEnabled) {
      const loadUserStats = async () => {
        const stats = await getUserTicketStats();
        setUserStats(stats);
      };
      loadUserStats();
    }
  }, [settings.limitActiveTicketsEnabled, getUserTicketStats]);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    const success = await saveSettings(formData);
    setSaving(false);

    if (success) {
      toastUtils.success(i18n.t("timeMonitoring.messages.saveTitle"), i18n.t('timeMonitoring.messages.saveSuccess'));
    }
  }, [formData, saveSettings]);

  const handleReset = useCallback(() => {
    setFormData(settings);
  }, [settings]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginTop: '16px' }}>
          {i18n.t("timeMonitoring.loading")}
        </Typography>
      </Box>
    );
  }

  const scopeOptions = [
    { value: "company", label: i18n.t("timeMonitoring.scopeCompany") },
    { value: "queue", label: i18n.t("timeMonitoring.scopeQueue") }
  ];

  const timeoutActionOptions = [
    { value: "user", label: i18n.t("timeMonitoring.transferToUser") },
    { value: "queue", label: i18n.t("timeMonitoring.transferToQueue") }
  ];

  const userOptions = users.map(user => ({
    value: user.id,
    label: user.name
  }));

  const queueOptions = queues.map(queue => ({
    value: queue.id,
    label: queue.name
  }));

  return (
    <Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <ScheduleIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t("timeMonitoring.title")}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t("timeMonitoring.description")}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? i18n.t("timeMonitoring.saving") : i18n.t("timeMonitoring.save")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              {i18n.t("timeMonitoring.reset")}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Configurações Básicas */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<TimerIcon />}
          title={i18n.t("timeMonitoring.basicSettings")}
          action={
            <IconButton onClick={() => toggleSection('basic')}>
              {expandedSections.basic ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.basic}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SettingSwitch
                  id="timeMonitoringEnabled"
                  title={i18n.t("timeMonitoring.enableMonitoring")}
                  description={i18n.t("timeMonitoring.enableMonitoringDesc")}
                  value={formData.timeMonitoringEnabled || false}
                  onChange={handleChange}
                  icon={<TimerIcon className={classes.sectionIcon} />}
                />
              </Grid>

              {formData.timeMonitoringEnabled && (
                <>
                  <Grid item xs={12} md={6}>
                    <SettingTextField
                      id="responseTimeLimit"
                      title={i18n.t("timeMonitoring.responseTimeLimit")}
                      description={i18n.t("timeMonitoring.responseTimeLimitDesc")}
                      value={formData.responseTimeLimit}
                      onChange={handleChange}
                      icon={<ScheduleIcon className={classes.sectionIcon} />}
                      type="number"
                      min={1}
                      max={480}
                      helperText={i18n.t("timeMonitoring.responseLimitHelper")}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <SettingSelect
                      id="timeMonitoringScope"
                      title={i18n.t("timeMonitoring.monitoringScope")}
                      description={i18n.t("timeMonitoring.monitoringScopeDesc")}
                      value={formData.timeMonitoringScope || 'company'}
                      options={scopeOptions}
                      onChange={handleChange}
                      icon={<AssessmentIcon className={classes.sectionIcon} />}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Configurações de Aviso */}
      {formData.timeMonitoringEnabled && (
        <Card className={classes.cardSection}>
          <CardHeader
            avatar={<WarningIcon />}
            title={i18n.t("timeMonitoring.warningSettings")}
            action={
              <IconButton onClick={() => toggleSection('warning')}>
                {expandedSections.warning ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.warning}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SettingSwitch
                    id="timeMonitoringWarning"
                    title={i18n.t("timeMonitoring.enableWarning")}
                    description={i18n.t("timeMonitoring.enableWarningDesc")}
                    value={formData.timeMonitoringWarning || false}
                    onChange={handleChange}
                    icon={<WarningIcon className={classes.sectionIcon} />}
                  />
                </Grid>

                {formData.timeMonitoringWarning && (
                  <Grid item xs={12} md={6}>
                    <SettingTextField
                      id="timeMonitoringWarningMinutes"
                      title={i18n.t("timeMonitoring.warningMinutes")}
                      description={i18n.t("timeMonitoring.warningMinutesDesc")}
                      value={formData.timeMonitoringWarningMinutes}
                      onChange={handleChange}
                      icon={<TimerIcon className={classes.sectionIcon} />}
                      type="number"
                      min={1}
                      max={60}
                      helperText={i18n.t("timeMonitoring.warningMinutesHelper")}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Ações de Timeout */}
      {formData.timeMonitoringEnabled && (
        <Card className={classes.cardSection}>
          <CardHeader
            avatar={<QueueIcon />}
            title={i18n.t("timeMonitoring.timeoutAction")}
            action={
              <IconButton onClick={() => toggleSection('actions')}>
                {expandedSections.actions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.actions}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SettingSelect
                    id="timeoutAction"
                    title={i18n.t("timeMonitoring.timeoutActionLabel")}
                    description={i18n.t("timeMonitoring.timeoutActionDesc")}
                    value={formData.timeoutAction || 'user'}
                    options={timeoutActionOptions}
                    onChange={handleChange}
                    icon={<QueueIcon className={classes.sectionIcon} />}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  {formData.timeoutAction === 'user' && (
                    <SettingSelect
                      id="timeoutTargetUserId"
                      title={i18n.t("timeMonitoring.targetUser")}
                      description={i18n.t("timeMonitoring.targetUserDesc")}
                      value={formData.timeoutTargetUserId || ''}
                      options={[{ value: '', label: i18n.t("timeMonitoring.selectUser") }, ...userOptions]}
                      onChange={handleChange}
                      icon={<PersonIcon className={classes.sectionIcon} />}
                    />
                  )}

                  {formData.timeoutAction === 'queue' && (
                    <SettingSelect
                      id="timeoutTargetQueueId"
                      title={i18n.t("timeMonitoring.targetQueue")}
                      description={i18n.t("timeMonitoring.targetQueueDesc")}
                      value={formData.timeoutTargetQueueId || ''}
                      options={[{ value: '', label: i18n.t("timeMonitoring.selectQueue") }, ...queueOptions]}
                      onChange={handleChange}
                      icon={<QueueIcon className={classes.sectionIcon} />}
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      )}

      {/* Configurações de Inatividade e Sessão */}
      <Card className={classes.cardSection}>
        <CardHeader
          avatar={<HourglassEmptyIcon />}
          title={i18n.t("timeMonitoring.inactivityMonitoring") || "Monitoramento de Inatividade & Sessão"}
          action={
            <IconButton onClick={() => toggleSection('inactivity')}>
              {expandedSections.inactivity ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.inactivity}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SettingSwitch
                  id="autoLogoutInactive"
                  title={i18n.t("timeMonitoring.autoLogoutInactive") || "Logout automático após inatividade"}
                  description="Quando ativado, os atendentes sem atividade pelo tempo determinado serão desconectados automaticamente por segurança. Se desativado, a sessão permanece ativa continuamente (até 30 dias com Refresh Token) sem desconectar por inatividade."
                  value={formData.autoLogoutInactive || false}
                  onChange={handleChange}
                  icon={<HourglassEmptyIcon className={classes.sectionIcon} />}
                />
              </Grid>

              {formData.autoLogoutInactive && (
                <>
                  <Grid item xs={12} md={6}>
                    <SettingTextField
                      id="inactivityTimeoutMinutes"
                      title={i18n.t("timeMonitoring.inactivityTimeout") || "Tempo para considerar inativo (minutos)"}
                      description="Informe o tempo em minutos antes de efetuar o logout (ex: 30 = 30min, 60 = 1 hora, 1440 = 24 horas)."
                      value={formData.inactivityTimeoutMinutes || 30}
                      onChange={handleChange}
                      icon={<ScheduleIcon className={classes.sectionIcon} />}
                      type="number"
                      min={1}
                      max={43200}
                      helperText={
                        formData.inactivityTimeoutMinutes
                          ? `Equivalente a: ${Math.floor(formData.inactivityTimeoutMinutes / 60)}h ${formData.inactivityTimeoutMinutes % 60}min`
                          : "Tempo em minutos"
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box className={classes.settingContainer} style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
                        Atalhos Rápidos de Duração:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
                        <Chip
                          label="30 minutos"
                          clickable
                          color={Number(formData.inactivityTimeoutMinutes) === 30 ? "primary" : "default"}
                          onClick={() => handleChange("inactivityTimeoutMinutes", 30)}
                        />
                        <Chip
                          label="1 hora (60 min)"
                          clickable
                          color={Number(formData.inactivityTimeoutMinutes) === 60 ? "primary" : "default"}
                          onClick={() => handleChange("inactivityTimeoutMinutes", 60)}
                        />
                        <Chip
                          label="4 horas (240 min)"
                          clickable
                          color={Number(formData.inactivityTimeoutMinutes) === 240 ? "primary" : "default"}
                          onClick={() => handleChange("inactivityTimeoutMinutes", 240)}
                        />
                        <Chip
                          label="8 horas (480 min)"
                          clickable
                          color={Number(formData.inactivityTimeoutMinutes) === 480 ? "primary" : "default"}
                          onClick={() => handleChange("inactivityTimeoutMinutes", 480)}
                        />
                        <Chip
                          label="24 horas (1440 min)"
                          clickable
                          color={Number(formData.inactivityTimeoutMinutes) === 1440 ? "primary" : "default"}
                          onClick={() => handleChange("inactivityTimeoutMinutes", 1440)}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Estatísticas */}
      {userStats && (
        <Card className={classes.cardSection}>
          <CardHeader
            avatar={<AssessmentIcon />}
            title={i18n.t("timeMonitoring.statistics")}
            action={
              <IconButton onClick={() => toggleSection('stats')}>
                {expandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            }
          />
          <Collapse in={expandedSections.stats}>
            <CardContent>
              <Box className={classes.statsCard} style={{ padding: '16px' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t("timeMonitoring.currentStats")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h6" color="primary">
                      {userStats.activeTickets || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("timeMonitoring.activeTickets")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h6" color="secondary">
                      {userStats.avgResponseTime || 0}min
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("timeMonitoring.avgResponseTime")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h6" style={{ color: '#f57c00' }}>
                      {userStats.timeoutCount || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("timeMonitoring.timeouts")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h6" style={{ color: '#388e3c' }}>
                      {userStats.successRate || 0}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("timeMonitoring.successRate")}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Collapse>
        </Card>
      )}
    </Box>
  );
};

export default TimeMonitoringSettings;